import { guest } from '@croissants/contracts';
import { SUPPORT_PAGE_PATH } from '@croissants/contracts/qr';
import { cx, RehearsalBar, Wordmark } from '@croissants/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { API_BASE_URL, legalLinks } from '../lib/config';
import { postalAddressLine } from '../lib/format';
import { menuTabHref } from '../lib/journey';
import { useBasket } from '../state/basket-provider';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';
import { DeliveryOfferLine } from './DeliveryOfferLine';
import { DeliveryTime } from './DeliveryTime';
import { SupportIcon } from './icons';
import { LanguageSwitch } from './LanguageSwitch';

/**
 * The app frame: brand, language switch, the apartment it is delivering to, and
 * the three-way navigation under them.
 *
 * Mobile-first is not a style here, it is the whole context - the guest is
 * standing in an unfamiliar flat holding a phone they just used as a QR scanner.
 * So targets clear 44px and the header stays a single line. The navigation sits
 * at the TOP, stuck to the viewport (founder decision, 2026-08-24): three words
 * crossed between twice an order read as the first thing on a screen, not the
 * last, and sticking them there is what keeps them in reach anyway.
 *
 * The legal links in the footer are required on every page (PRD WEB-007) and are
 * built from configuration, never from a literal host (PRD OPS-018) - which is
 * also why they are simply absent when no public site is configured, rather than
 * pointing somewhere that does not exist.
 */
/**
 * The guest queries a platform-clock move can change the answer to (TST-003).
 *
 * Named rather than swept: `invalidateQueries()` with no filter is refused by
 * `scripts/check-write-refetch.mjs`, and it would be wrong anyway - the basket
 * and the quote are the guest's own working state, not something the rehearsal
 * clock decides.
 */
const REHEARSAL_SENSITIVE_PATHS = new Set(
  [guest.listWindows, guest.getStorefront, guest.listOrders, guest.getOrder].map(
    (endpoint) => endpoint.path,
  ),
);

/**
 * The QR session is the one read still keyed by hand (`state/session.tsx`): it
 * resolves the code, writes the guest key to storage and hands the token to the
 * client module, none of which is a plain endpoint read.
 */
const SESSION_KEY = 'session';

export function Shell({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const { t, locale } = useLocale();
  const { session } = useSession();
  const { count } = useBasket();
  const [location] = useLocation();
  const links = legalLinks(locale);
  const frame = useRef<HTMLDivElement | null>(null);
  const [navbar, setNavbar] = useState<HTMLElement | null>(null);

  /**
   * What the navigation measures, published as `--gw-navbar-h`.
   *
   * The navigation is the one bar that stays stuck to the top of the viewport,
   * so a screen pinning something of its own under it - the picker's day strip
   * (GST-002) - has to know where it ends. That is not a constant: the tabs
   * step their type down on a narrow phone, "Mes commandes" is not "Orders",
   * and on a screen with no session there is no bar at all. Written down as a
   * number it would be right on the phone it was measured on and a few pixels
   * out everywhere else, which shows as a sliver of the list passing through
   * the gap, or as a strip sitting half under the bar. Measured, it is right,
   * and it stays right when the bar is not there (`0px`).
   */
  useEffect(() => {
    const root = frame.current;
    if (root === null) return undefined;
    if (navbar === null) {
      root.style.setProperty('--gw-navbar-h', '0px');
      return undefined;
    }
    const publish = (): void => {
      root.style.setProperty(
        '--gw-navbar-h',
        `${Math.round(navbar.getBoundingClientRect().height)}px`,
      );
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(navbar);
    return () => observer.disconnect();
  }, [navbar]);

  /**
   * A screen opens at ITS top, and only a step BACK returns to where the guest
   * had got to.
   *
   * A history push keeps the browser's scroll offset, which is right for a
   * document and wrong for a journey: every screen here replaces the one before
   * it, so the offset carried across belongs to a page that is gone. The screens
   * this app pushes between are long and are pressed from the bottom - the
   * basket's button sits after the lines, the picker's after the half-hours -
   * and the screen that replaces them is usually already in the cache, so it
   * renders full height in one frame and there is no short moment for the
   * browser to clamp the offset against. Measured on a 640px viewport: the
   * picker opened 400px down, on its own footer, with the day strip off the top
   * of the screen; confirming from there opened the review 304px down.
   *
   * A POP is left alone, deliberately. Back means "put me where I was", the
   * browser already restores that, and taking a guest who backed out of the
   * review to the top of a menu they had scrolled halfway down is the same
   * defect pointing the other way. The flag is set by the event that precedes
   * the location change and cleared by the render that follows it.
   */
  const poppedBack = useRef(false);
  const shownScreen = useRef(location);
  useEffect(() => {
    const onPop = (): void => {
      poppedBack.current = true;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    /* A screen the guest is already on, re-rendered. Nothing moved, so nothing
       moves - and the first render is one of these, which is what leaves a
       reloaded page where the browser itself put it. */
    if (shownScreen.current === location) return;
    shownScreen.current = location;
    const wasBack = poppedBack.current;
    poppedBack.current = false;
    if (wasBack) return;
    window.scrollTo({ top: 0, left: 0 });
  }, [location]);

  const showNav = session === null ? false : session.servable && !session.servicePaused;

  return (
    <div className="cib-root gw-app" ref={frame}>
      <a className="gw-skiplink" href="#main">
        {t('app.skipToContent')}
      </a>

      <header className="gw-appbar">
        <Link href="/" className="gw-appbar__brand" aria-label="Croissant in Bed">
          <Wordmark size={34} title={null} />
        </Link>
        {/* ONE group, so the bar stays two items and its give-way order holds:
            the wordmark yields, the tools never do. The flag first, the way to
            reach us at its right (founder, 2026-09-18). */}
        <div className="gw-appbar__tools">
          <LanguageSwitch />
          <Link
            href={SUPPORT_PAGE_PATH}
            className="gw-appbar__support"
            aria-label={t('support.title')}
          >
            <SupportIcon />
          </Link>
        </div>
      </header>

      {/*
        The STREET, and not the name of the flat (founder decision,
        2026-08-23). "Le Marais Loft" names the apartment to the owner who chose
        the label and to nobody else - the guest scanned a code inside it and
        does not need telling which room they are in. What this line is for is
        the address the courier is being sent to, which is the part they can
        check against the door they are standing behind. It is already on the
        session, so no request pays for it.

        Still GATED on the label rather than on the address, which is not an
        inconsistency: the placeholder session a defined-but-unserved screen
        gets carries "n/a" in every address field, and an empty label is what
        marks it. The label decides whether there is a banner; it is no longer
        in it.
      */}
      {session !== null && session.propertyLabel !== '' ? (
        <p className="gw-appbar__place">
          {t('landing.deliveringTo', { place: postalAddressLine(session.address) })}
          {/* GST-045: mode B2's chosen delivery time and the way to change it,
              directly under the address it completes - "Delivering to ... / in
              about 30 min" - and ABOVE the free-delivery line (founder,
              2026-09-21). */}
          <DeliveryTime />
          {/* GST-044: under the address, on every screen (founder, 21 September
              2026) - the free-delivery basket, and once the basket holds
              anything what the next step of the grid saves, in green. */}
          <DeliveryOfferLine />
        </p>
      ) : null}

      {/* At the TOP, above the content it navigates (founder decision,
          2026-08-24). It sat at the bottom, in thumb reach, which is the
          convention for a bar of five icons a guest returns to constantly;
          this is three words a guest crosses between twice an order, and at
          the bottom it was the last thing read on every screen rather than the
          first. It sticks to the top of the viewport, so it is reachable from
          anywhere down a long menu - the brand bar above it scrolls away with
          the address, which are both read once. */}
      {showNav ? (
        <nav className="gw-tabbar" aria-label={t('nav.label')} ref={setNavbar}>
          <TabLink
            href={menuTabHref(session?.storefrontMode ?? null, location)}
            active={isMenuRoute(location)}
            label={t('nav.menu')}
          />
          <TabLink
            href="/basket"
            active={location.startsWith('/basket')}
            label={t('nav.basket')}
            badge={count}
            badgeLabel={t('nav.basketCount', { count })}
          />
          <TabLink href="/orders" active={location.startsWith('/orders')} label={t('nav.orders')} />
        </nav>
      ) : null}

      <main id="main" className="cib-main" aria-label={t('app.mainLabel')}>
        {children}
      </main>

      <footer className="gw-footer">
        <nav className="gw-footer__legal" aria-label={t('footer.legalLabel')}>
          <a href={links.terms}>{t('checkout.legal.terms')}</a>
          <a href={links.howItWorks}>{t('checkout.legal.howItWorks')}</a>
          {links.privacy === null ? null : (
            <a href={links.privacy}>{t('checkout.legal.privacy')}</a>
          )}
          {links.legal === null ? null : <a href={links.legal}>{t('checkout.legal.legal')}</a>}
        </nav>
      </footer>

      {/* The rehearsal strip (TST-003). Dev builds only, and it renders nothing
          at all unless this API serves /dev/rehearsal. */}
      {import.meta.env.DEV ? (
        <RehearsalBar
          apiBaseUrl={API_BASE_URL}
          onPlatformTimeChanged={() =>
            void queryClient.invalidateQueries({
              predicate: (query) =>
                query.queryKey[0] === SESSION_KEY ||
                REHEARSAL_SENSITIVE_PATHS.has(String(query.queryKey[1])),
            })
          }
        />
      ) : null}
    </div>
  );
}

function isMenuRoute(location: string): boolean {
  return location === '/' || location.startsWith('/menu') || location.startsWith('/shops');
}

function TabLink({
  href,
  active,
  label,
  badge,
  badgeLabel,
}: {
  href: string;
  active: boolean;
  label: string;
  badge?: number;
  badgeLabel?: string;
}): JSX.Element {
  return (
    <Link
      href={href}
      className={cx('gw-tabbar__item', active && 'is-active')}
      aria-current={active ? 'page' : undefined}
      aria-label={badge !== undefined && badge > 0 ? badgeLabel : undefined}
    >
      <span>{label}</span>
      {badge !== undefined && badge > 0 ? (
        <span className="gw-tabbar__badge" aria-hidden="true">
          {badge}
        </span>
      ) : null}
    </Link>
  );
}
